(() => {
  if (customElements.get('cam-account-login')) return;
  const storage = {
    read(type, key) { try { return window[type].getItem(key); } catch { return null; } },
    write(type, key, value) { try { window[type].setItem(key, value); } catch {} }
  };
  const normalisePhone = raw => {
    let digits = String(raw || '').replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
    if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
    return /^[6-9]\d{9}$/.test(digits) ? digits : '';
  };
  // Numbers outside India: digits only, 6-14 long, without leading zeros.
  const normaliseIntl = raw => {
    const digits = String(raw || '').replace(/\D/g, '').replace(/^0+/, '');
    return /^\d{6,14}$/.test(digits) ? digits : '';
  };
  customElements.define('cam-account-login', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const trigger = this.querySelector('.cam-account-icon');
      const dialog = this.querySelector('dialog');
      if (!trigger || !dialog || typeof dialog.showModal !== 'function') return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const key = 'cam_popup_' + this.dataset.campaign;
      const frequency = this.dataset.frequency || 'days';
      const editor = this.dataset.editor === 'true';
      const seen = () => {
        if (frequency === 'page') return false;
        if (frequency === 'once') return !!storage.read('localStorage', key);
        // Once per visit: not again while moving between pages, but again after a refresh or when the site is reopened.
        if (frequency === 'visit') {
          const navigation = performance.getEntriesByType?.('navigation')?.[0];
          if (navigation && navigation.type === 'reload' && !this.shownThisLoad) return false;
          return !!storage.read('sessionStorage', key);
        }
        if (frequency === 'session') return !!storage.read('sessionStorage', key);
        const timestamp = Number(storage.read('localStorage', key));
        return timestamp > 0 && Date.now() - timestamp < Number(this.dataset.repeatDays || 7) * 86400000;
      };
      const markSeen = () => {
        if (editor) return;
        storage.write('sessionStorage', key, '1');
        storage.write('localStorage', key, String(Date.now()));
      };
      const unlock = () => {
        if (!this.locked) return;
        document.documentElement.style.overflow = this.previousOverflow;
        this.locked = false;
      };
      this.cleanupModal = unlock;
      const open = () => {
        if (dialog.open) return;
        clearTimeout(this.autoTimer);
        this.returnFocus = document.activeElement;
        dialog.showModal();
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.locked = true;
        this.shownThisLoad = true;
        markSeen();
      };
      const close = () => { if (dialog.open) dialog.close(); };
      trigger.addEventListener('click', event => {
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        open();
      }, options);
      dialog.querySelector('.cam-account-close').addEventListener('click', close, options);
      dialog.addEventListener('close', () => {
        unlock();
        const focus = this.returnFocus;
        if (focus && focus.isConnected && focus !== document.body) focus.focus({ preventScroll: true });
      }, options);
      dialog.addEventListener('click', event => {
        if (this.dataset.backdropClose !== 'true' || event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) close();
      }, options);
      const form = dialog.querySelector('[data-phone-form]');
      const input = form.elements.phone;
      const error = form.querySelector('.cam-account-error');
      const invalidMessage = error.textContent;
      const ccSelect = form.elements.country_code;
      const ccDisplay = form.querySelector('[data-cc-display]');
      const ccFlag = form.querySelector('[data-cc-flag]');
      const countryCode = () => (ccSelect ? ccSelect.value : '') || this.dataset.countryCode || '+91';
      const isIndia = () => countryCode() === '+91';
      const normalise = value => (isIndia() ? normalisePhone(value) : normaliseIntl(value));
      const syncCountry = () => {
        if (ccDisplay) ccDisplay.textContent = countryCode();
        if (ccFlag) ccFlag.remove();
        input.placeholder = isIndia() ? input.dataset.placeholderIn : input.dataset.placeholderIntl;
      };
      const savedCode = storage.read('localStorage', 'cam_phone_cc');
      if (ccSelect && savedCode && [...ccSelect.options].some(option => option.value === savedCode)) ccSelect.value = savedCode;
      syncCountry();
      ccSelect?.addEventListener('change', () => {
        syncCountry();
        error.hidden = true;
        input.removeAttribute('aria-invalid');
      }, options);
      const saved = storage.read('localStorage', 'cam_phone');
      if (saved) input.value = normalise(saved);
      input.addEventListener('input', () => {
        error.hidden = true;
        input.removeAttribute('aria-invalid');
      }, options);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (form.getAttribute('aria-busy') === 'true') return;
        const phone = normalise(input.value);
        if (!phone) {
          error.textContent = isIndia() ? invalidMessage : error.dataset.errorIntl;
          error.hidden = false;
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        // Lead capture: the number also goes to the store's private database; a failure never blocks the popup.
        const leadEndpoint = this.dataset.leadEndpoint;
        if (leadEndpoint && !editor) {
          try {
            fetch(leadEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'text/plain' },
              body: JSON.stringify({ type: 'lead', country_code: countryCode(), number: phone, page: location.pathname }),
              keepalive: true
            }).catch(() => {});
          } catch {}
        }
        const button = form.querySelector('button[type="submit"]');
        error.hidden = true;
        input.removeAttribute('aria-invalid');
        button.disabled = true;
        form.setAttribute('aria-busy', 'true');
        this.requestController = new AbortController();
        const timeout = setTimeout(() => this.requestController?.abort(), 10000);
        try {
          const response = await fetch(this.dataset.cartUpdate, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { 'Mobile number': countryCode() + ' ' + phone } }),
            signal: this.requestController.signal
          });
          if (!response.ok) throw new Error('Save failed');
          storage.write('localStorage', 'cam_phone', phone);
          storage.write('localStorage', 'cam_phone_cc', countryCode());
          input.value = phone;
          close();
        } catch {
          if (this.isConnected) {
            error.textContent = this.dataset.saveError;
            error.hidden = false;
          }
        } finally {
          clearTimeout(timeout);
          button.disabled = false;
          form.removeAttribute('aria-busy');
        }
      }, options);
      if (editor) {
        document.addEventListener('shopify:block:select', event => {
          if (event.detail.blockId === this.dataset.blockId) open();
        }, options);
        document.addEventListener('shopify:block:deselect', event => {
          if (event.detail.blockId === this.dataset.blockId) close();
        }, options);
        if (this.dataset.preview === 'true') open();
      } else {
        const delay = Number(this.dataset.autoshow);
        const skipSaved = saved && this.dataset.skipSaved !== 'false';
        if (Number.isFinite(delay) && delay > 0 && !seen() && !skipSaved) {
          const attempt = () => {
            if (!this.isConnected || seen()) return;
            if (document.hidden || document.querySelector('dialog[open]')) {
              this.autoTimer = setTimeout(attempt, 1000);
              return;
            }
            open();
          };
          this.autoTimer = setTimeout(attempt, delay * 1000);
        }
      }
    }
    disconnectedCallback() {
      clearTimeout(this.autoTimer);
      this.requestController?.abort();
      this.cleanupModal?.();
      this.controller?.abort();
      this.controller = null;
    }
  });
})();



